import React, { useState, useRef, useEffect, memo } from "react";
import debounce from "lodash/debounce";
import moment from "moment";
import cx from "classnames";
import { FavouriteIcon, CloseIcon } from "../library/Icons";
import "./TaskList.css";
import Button from "../library/Button";

export const HOLIDAY = "bbbbbbbbbbbbbbbbbbbbbbbb";
export const PUBLIC_HOLIDAY = "000000000000000000000503";
export const SICK = "cccccccccccccccccccccccc";

const TIME_OFF_IDS = [HOLIDAY, PUBLIC_HOLIDAY, SICK];

const TaskListItem = ({
  task,
  tasks,
  totalTime,
  autoFocus,
  isShiftPressed,
  onUpdateTaskMinutes,
  onFinishedTaskTimeEdit,
  onAddFavourite,
  onRemoveFavourite,
  onRemoveTask,
  onStartTimer,
  onStopTimer,
  timerRunning,
  requiredMinutes = 450,
  userId,
  isApproved,
}) => {
  const [taskTotalMinutes, setTaskTotalMinutes] = useState(task.minutes || 0);
  const [savedTotalMinutes, setSavedTotalMinutes] = useState(task.minutes || 0);
  const [prevTask, setPrevTask] = useState(task);

  useEffect(() => {
    // If task data from API has changed update the inputs to match saved data
    if (task !== prevTask && task.minutes !== prevTask.minutes) {
      setPrevTask(task);
      setTaskTotalMinutes(task.minutes || 0);
      setSavedTotalMinutes(task.minutes || 0);
    }
  }, [task, prevTask]);

  const isTimeOff = TIME_OFF_IDS.indexOf(task.job) > -1;

  const isToday =
    moment(task.date).utc().format("YYYY-MM-DD") ===
    moment().format("YYYY-MM-DD");

  const hours = Math.floor(taskTotalMinutes / 60);
  const minutes = taskTotalMinutes - hours * 60;

  const setHoursFromInput = (e) => {
    const newHours =
      Math.max(0, Math.min(23, parseInt(e.currentTarget.value, 10))) || 0;
    setTaskTotalMinutes(Math.max(0, newHours * 60 + minutes));
  };

  const setMinutesFromInput = (e) => {
    const newMinutes =
      Math.max(-15, Math.min(60, parseInt(e.currentTarget.value, 10))) || 0;
    setTaskTotalMinutes(Math.max(0, hours * 60 + newMinutes));
  };

  const debouncedUpdateTime = useRef(
    debounce((newMinutes) => {
      onUpdateTaskMinutes(task.id, newMinutes);
    }, 500)
  );

  const setFillDay = () => {
    const totalTime = tasks
      .filter((t) => t.id !== task.id)
      .reduce((memo, t) => {
        return memo + t.minutes;
      }, 0);
    const fillTime = requiredMinutes - totalTime;
    setTaskTotalMinutes(fillTime);
    onFinishedTaskTimeEdit();
  };

  const onToggleFavourite = (e) => {
    if (!task.isFavourite) {
      onAddFavourite(task.job, taskTotalMinutes);
    } else {
      onRemoveFavourite(task.job);
    }
  };

  const onRemove = () => {
    if (window.confirm(`Remove task "${task.name}"?`)) {
      onRemoveTask(task.id);
    }
  };

  const onStart = async () => {
    await onStopTimer();
    onStartTimer(task.id, task.job, task.name);
  };

  useEffect(() => {
    if (taskTotalMinutes !== savedTotalMinutes) {
      debouncedUpdateTime.current(taskTotalMinutes);
      setSavedTotalMinutes(taskTotalMinutes);
    }
  }, [taskTotalMinutes, savedTotalMinutes]);

  return (
    <div data-testid="tasklist-item" className="TaskListItem">
      <div className="TaskListItem__Left">
        {!userId ? (
          <button
            data-testid="favourite-button"
            className={cx("TaskListItem__FavouriteButton", {
              "TaskListItem__FavouriteButton--Active": task.isFavourite,
            })}
            title={
              task.isFavourite ? "Remove from favourites" : "Add as favourite"
            }
            onClick={onToggleFavourite}
          >
            <FavouriteIcon />
          </button>
        ) : (
          <div className="TaskListItem__NoFavouriteOffset" />
        )}
        <div data-testid="task-name" className="TaskListItem__Name">
          {task.name}
        </div>
      </div>
      <div
        className={`TaskListItem__Right ${isApproved ? "TaskListItem__Right--approvedOffset" : ""
          }`}
      >
        <input
          id={`${task.id}_hours`}
          type="number"
          min="0"
          max="23"
          value={hours || ""}
          disabled={isApproved}
          title={
            (isApproved && "Unable to edit an approved day") ||
            ""
          }
          placeholder="0"
          onChange={setHoursFromInput}
          autoFocus={autoFocus}
          onBlur={onFinishedTaskTimeEdit}
        />
        <label htmlFor={`${task.id}_hours`}>hrs</label>
        <input
          id={`${task.id}_minutes`}
          type="number"
          min="-15"
          max="60"
          step={isShiftPressed ? 5 : 15}
          value={minutes || ""}
          disabled={isApproved}
          title={
            (isApproved && "Unable to edit an approved day") ||
            "Hold Shift ±5"
          }
          placeholder="0"
          onChange={setMinutesFromInput}
          onBlur={onFinishedTaskTimeEdit}
        />
        <label htmlFor={`${task.id}_minutes`}>mins</label>

        {(isTimeOff || !isToday) && !isApproved && (
          <div className="TaskListItem__TimerButton">
            {totalTime < requiredMinutes && (
              <Button onClick={setFillDay}>Fill Day</Button>
            )}
          </div>
        )}

        {!isTimeOff && isToday && (
          <div className="TaskListItem__TimerButton">
            {timerRunning ? (
              <Button danger onClick={onStopTimer}>
                Stop Timer
              </Button>
            ) : (
              <Button success onClick={onStart}>
                Start Timer
              </Button>
            )}
          </div>
        )}

        {!isApproved ? (
          <button
            data-testid="delete-task"
            className="TaskListItem__CloseButton"
            onClick={onRemove}
            title={`Remove task "${task.name}"`}
          >
            <CloseIcon />
          </button>
        ) : (
          <div style={{ width: 44 }} />
        )}
      </div>
    </div>
  );
};

const TaskList = ({
  tasks,
  trackingTask,
  onUpdateTaskMinutes,
  onFinishedTaskTimeEdit,
  onAddFavourite,
  onRemoveFavourite,
  onRemoveTask,
  onStartTimer,
  onStopTimer,
  requiredMinutes = 450,
  userId,
  isApproved,
}) => {
  const [isShiftPressed, setIsShiftPressed] = useState(false);

  const totalTime = tasks.reduce((memo, task) => memo + task.minutes, 0);
  const totalHours = Math.floor(totalTime / 60);
  const totalMinutes = totalTime - totalHours * 60;

  const isToday =
    tasks[0] &&
    moment(tasks[0].date).utc().format("YYYY-MM-DD") ===
    moment().format("YYYY-MM-DD");

  const handleKeydown = (e) => {
    if (e.key === "Shift") {
      setIsShiftPressed(true);
    }
  };

  const handleKeyup = (e) => {
    if (e.key === "Shift") {
      setIsShiftPressed(false);
    }
  };

  useEffect(() => {
    document.addEventListener("keydown", handleKeydown);
    document.addEventListener("keyup", handleKeyup);

    return () => {
      document.removeEventListener("keydown", handleKeydown);
      document.removeEventListener("keyup", handleKeyup);
    };
  }, []);

  return (
    <div className="TaskList">
      {tasks.map((task, taskIndex) => (
        <TaskListItem
          task={task}
          tasks={tasks}
          totalTime={totalTime}
          key={task.id}
          isShiftPressed={isShiftPressed}
          timerRunning={trackingTask && trackingTask.id === task.id}
          onUpdateTaskMinutes={onUpdateTaskMinutes}
          onFinishedTaskTimeEdit={onFinishedTaskTimeEdit}
          onAddFavourite={onAddFavourite}
          onRemoveFavourite={onRemoveFavourite}
          onRemoveTask={onRemoveTask}
          onStartTimer={onStartTimer}
          onStopTimer={onStopTimer}
          autoFocus={!trackingTask && taskIndex === tasks.length - 1}
          isToday={isToday}
          requiredMinutes={requiredMinutes}
          userId={userId}
          isApproved={isApproved}
        />
      ))}
      {tasks.length === 0 && (
        <div className="TaskList__Empty">No tasks added</div>
      )}
      {tasks.length > 0 && (
        <div className="TaskList__Totals">
          <div className="TaskList__TotalsLabel">Total</div>
          <div
            data-testid="tasklist-total"
            className={cx("TaskList__TotalsTime", {
              "TaskList__TotalsTime--Low":
                !isToday && totalTime < requiredMinutes,
            })}
          >
            <strong>{totalHours}</strong>
            <small>hrs</small>
            <strong>{totalMinutes}</strong>
            <small>mins</small>
          </div>
          <div className="TaskList__TotalsOffset" />
        </div>
      )}
    </div>
  );
};

export default TaskList;
